import { useState } from "react"
import { Eye, EyeOff, ArrowLeft } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { loginSchema, type LoginData } from "@/lib/validationSchemas"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import { FormSubmitButton } from "@/components/ui/form-submit-button"
import { useNavigate, Link } from "react-router-dom"
import { toastSuccess, toastError } from "@/lib/toast"
import { BrandLogo } from "@/components/ui/brand-logo"
import { useAuth } from "@/hooks/useAuth"
import { GoogleLogin } from "@react-oauth/google"
import { FormInputField } from "@/components/ui/form-input-field"
import { useLanguage } from "@/hooks/useLanguage"

const Login = () => {
  const { t } = useLanguage()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { login, externalLogin } = useAuth()

  const text = t.login

  const form = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const onSubmit = async (data: LoginData) => {
    setLoading(true)

    try {
      await login({
        email: data.email,
        password: data.password,
      })
      toastSuccess(text.success)
      navigate("/catalog/api")
    } catch (error: any) {
      const message = error?.code === "email_not_confirmed" ? text.emailNotConfirmed : error.message || text.error
      toastError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true)
    try {
      await externalLogin("Google", credentialResponse.credential)
      toastSuccess(text.success)
      navigate("/catalog/api")
    } catch (error: any) {
      toastError(text.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4 font-sans text-foreground transition-colors duration-300">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-4 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {text.backHome}
          </Button>

          <div className="mb-6 flex items-center justify-center space-x-3">
            <BrandLogo className="h-16" />
            <span className="font-display text-3xl tracking-display text-foreground">MEATBID</span>
          </div>
          <div className="space-y-4">
            <h1 className="font-display text-4xl uppercase leading-none tracking-tighter text-foreground">
              {text.welcome}
            </h1>
            <div className="mx-auto h-1 w-12 bg-primary" />
            <p className="text-xs font-black uppercase tracking-label text-muted-foreground">
              {text.subtitle}
            </p>
          </div>
        </div>

        {/* Form Grid Container */}
        <div className="overflow-hidden bg-foreground/10 p-px shadow-2xl">
          <div className="space-y-8 bg-background p-8 md:p-10">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormInputField
                  control={form.control}
                  name="email"
                  label={text.email}
                  placeholder="email@example.com"
                />

                <FormInputField
                  control={form.control}
                  name="password"
                  label={text.password}
                  type={showPassword ? "text" : "password"}
                  rightElement={
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="px-3 text-muted-foreground hover:bg-transparent hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  }
                />

                <FormSubmitButton loading={loading}>{text.signIn}</FormSubmitButton>
              </form>
            </Form>

            <div className="flex items-center gap-4 py-2">
              <div className="h-px flex-1 bg-foreground/10"></div>
              <span className="whitespace-nowrap text-xs font-black uppercase tracking-label text-muted-foreground">
                {text.or}
              </span>
              <div className="h-px flex-1 bg-foreground/10"></div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  toastError("Google login failed")
                }}
                useOneTap
                theme="outline"
                width="100%"
              />
            </div>

            <div className="pt-4 text-center">
              <p className="text-xs font-black uppercase tracking-label text-muted-foreground">
                {text.noAccount}{" "}
                <Link to="/buyer-signup" className="text-primary hover:underline">
                  {text.signUp}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Login

